import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getAbsencesData, getPendingAbsencesForReview } from "@/Api/absences";
import { AbsenceStatusConst, AbsenceStatusLabel } from "@/types/absence";
import { DecideAbsenceBtns } from "@/App/Absences/Decide";
import { CancelAbsenceBtn } from "@/App/Absences/Cancel";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal, CalendarOff, AlertTriangle } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";
import { If } from "@/components/If";

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

const formatPeriod = (item: any): string => {
  if (item.start_time && item.end_time) {
    return `${formatDate(item.start_date)} ${item.start_time.slice(0, 5)}-${item.end_time.slice(0, 5)}`;
  }
  if (item.start_date === item.end_date) {
    return formatDate(item.start_date);
  }
  return `${formatDate(item.start_date)} a ${formatDate(item.end_date)}`;
};

export const PageList = () => {
  const [pending, setPending] = useState<any[]>([]);
  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const { hasPermission } = usePermissions();
  const unityFilter = useUnityFilter();
  const canDecide = hasPermission("decide-absence-request");

  const fetchPending = async () => {
    if (!canDecide) return;
    const { data } = await getPendingAbsencesForReview(1, unityFilter.param, unityFilter.nameParam);
    setPending(data?.response?.data ?? []);
  };

  const fetchList = async (page: number = 1) => {
    setState("loading");
    const { data } = await getAbsencesData(page, unityFilter.param, unityFilter.nameParam);
    setListData(data?.response ?? {});
    setState("production");
  };

  useEffect(() => {
    if (!unityFilter.ready) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unityFilter.ready, unityFilter.param, unityFilter.nameParam]);

  // Permissões carregam depois da montagem: refaz a busca quando canDecide muda
  useEffect(() => {
    if (!unityFilter.ready) return;
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDecide, unityFilter.ready, unityFilter.param, unityFilter.nameParam]);

  const refetchAll = () => {
    fetchPending();
    fetchList(listData.current_page || 1);
  };

  const pageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchList(listData.current_page + 1);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchList(listData.current_page - 1);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchList(listData.last_page);
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-absences-list")}>
        <PageHeader
          icon={CalendarOff}
          title="Abonos"
          description="Acompanhe e decida os abonos solicitados pelos colaboradores"
        />

        <UnityFilter
          filter={unityFilter}
          className="mb-3"
          namePlaceholder="Buscar por colaborador"
        />

        {canDecide && pending.length > 0 && (
          <div className="flex flex-col gap-2 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
            <label className="text-sm font-medium flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="size-4" /> Pendentes de decisão
            </label>
            {pending.map((item: any) => (
              <div
                key={item.uuid}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]"
              >
                <div className="text-sm">
                  <div className="font-semibold">
                    {item.employee?.user?.name} — {item.absence_type?.name} (
                    {formatPeriod(item)})
                  </div>
                  <div className="text-muted-foreground">
                    {item.justification}
                  </div>
                </div>
                <DecideAbsenceBtns uuid={item.uuid} onDecided={refetchAll} />
              </div>
            ))}
          </div>
        )}

        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : (
          <>
            <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
              <TableHeader>
                <TableRow>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Justificativa</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listData?.data?.map((item: any) => (
                  <TableRow key={item.uuid}>
                    <TableCell>{item.employee?.user?.name}</TableCell>
                    <TableCell>{item.absence_type?.name}</TableCell>
                    <TableCell>{formatPeriod(item)}</TableCell>
                    <TableCell className="truncate max-w-80">
                      {item.justification}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={
                          item.status === AbsenceStatusConst.APPROVED
                            ? "default"
                            : item.status === AbsenceStatusConst.REJECTED ||
                                item.status === AbsenceStatusConst.CANCELLED
                              ? "destructive"
                              : "outline"
                        }
                      >
                        {AbsenceStatusLabel[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="flex justify-center">
                      {canDecide &&
                        (item.status === AbsenceStatusConst.PENDING ||
                          item.status === AbsenceStatusConst.APPROVED) && (
                          <CancelAbsenceBtn
                            uuid={item.uuid}
                            onCancelled={refetchAll}
                          />
                        )}
                    </TableCell>
                  </TableRow>
                ))}
                {(!listData?.data || listData.data.length < 1) && (
                  <TableRow>
                    <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex justify-between mb-3 mt-2">
              {listData?.data?.length > 0 && (
                <span className="font-medium">
                  Exibindo {listData?.current_page} de um total de{" "}
                  {listData?.last_page}
                </span>
              )}
              {listData?.last_page > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={pageActions.previous}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                      >
                        <span className="pointer-events-none dark:text-[#F4F4F5]">
                          {listData?.current_page}
                        </span>
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className="text-black h-8 hover:bg-stone-950/10"
                        onClick={pageActions.lastPage}
                      >
                        <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={pageActions.next}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </If>
    </Main>
  );
};

export default PageList;
